import { Ability, AbilityBuilder, AbilityClass, PureAbility, Subject } from '@casl/ability';
import { Subjects } from './subjects';
import type { AbilityUser, Action, AppAbility } from './types';

// Clase de habilidad de la aplicación
export const AppAbilityClass = Ability as AbilityClass<AppAbility>;

/**
 * Define las habilidades (permisos) basadas en el usuario
 * Esta función es compartida entre Web y Mobile
 */
export function defineAbilitiesFor(user: AbilityUser | null): AppAbility {
  const { can, cannot, build } = new AbilityBuilder<PureAbility<[Action, Subject]>>(AppAbilityClass);

  if (!user) {
    // Usuario no autenticado - sin permisos
    return build();
  }

  switch (user.role) {
    case 'ADMIN':
      defineAdminAbilities(can);
      break;
    case 'SECTION_ADMIN':
      defineSectionAdminAbilities(can);
      break;
    case 'GUARD':
      defineGuardAbilities(can, cannot, user);
      break;
    case 'FAMILIAR':
      defineFamiliarAbilities(can, cannot, user);
      break;
    case 'CUSTOM':
      break;
    case 'RESIDENT':
    default:
      defineResidentAbilities(can, cannot, user);
      break;
  }

  user.abilities?.forEach((ability) => {
    if (ability.inverted) {
      cannot(ability.action as Action, ability.subject as Subject, ability.conditions);
      return;
    }
    can(ability.action as Action, ability.subject as Subject, ability.conditions);
  });

  return build();
}

type CanFunction = AbilityBuilder<PureAbility<[Action, Subject]>>['can'];
type CannotFunction = AbilityBuilder<PureAbility<[Action, Subject]>>['cannot'];

/**
 * Permisos de Administrador - acceso total
 */
function defineAdminAbilities(can: CanFunction) {
  can('manage', Subjects.All);
}

/**
 * Permisos del Administrador de Sección - misma superficie que un ADMIN pero
 * acotada a su(s) sección(es). El detalle de aislamiento lo impone el backend
 * (SectionScopeService); aquí solo se habilitan las pantallas de la UI.
 */
function defineSectionAdminAbilities(can: CanFunction) {
  can('read', Subjects.Dashboard);

  // Secciones - solo lectura: editar, cambiar estado y nombrar admins es del ADMIN (§25.3)
  can('read', Subjects.Section);

  // Unidades de su sección
  can('read', Subjects.Unit);
  can('manage', Subjects.Unit);

  // Residentes de su sección
  // Alta, edición y baja: el backend acota a residentes de su sección.
  can('manage', Subjects.ResidentMenu);
  can('read', Subjects.User);
  can('update', Subjects.User);
  can('create', Subjects.User);
  can('delete', Subjects.User);

  // Personal y roles propios de su sección (§25.10, §25.11); el backend acota
  // a `member_sections` y a `roles.section_id`.
  can('manage', Subjects.StaffMenu);
  can('manage', Subjects.RoleMenu);
  can('manage', Subjects.Role);

  // Comunicados y encuestas de su sección
  can('manage', Subjects.Announcement);
  can('manage', Subjects.Poll);

  // Control de acceso (puertas y registro) - solo lectura en su sección
  can('read', Subjects.AccessControl);
  can('read', Subjects.AccessDoor);
  can('read', Subjects.AccessLog);

  // Resto del panel con paridad de ADMIN; el backend acota cada registro a la
  // sección. Debe coincidir con defineSectionAdminAbilities del backend.
  can('manage', Subjects.Charge);
  can('manage', Subjects.Payment);
  can('manage', Subjects.Guest);
  can('manage', Subjects.Ticket);
  can('manage', Subjects.Amenity);
  can('manage', Subjects.Reservation);
  can('manage', Subjects.Document);
  can('manage', Subjects.TransparencyProject);
  can('manage', Subjects.TransparencyComment);
  can('manage', Subjects.Pet);
  can('manage', Subjects.Vehicle);
  can('manage', Subjects.Restriction);

  // Finanzas con paridad de ADMIN (§25): el backend acota gastos, cobranza,
  // reportes y resumen a su sección. Categorías es catálogo del residencial:
  // solo lectura.
  can('manage', Subjects.Expense);
  can('manage', Subjects.Cobranza);
  can('read', Subjects.Report);
  can('read', 'Summary');
  can('read', 'Category');

  // Operaciones y Configuración: tarjetas RFID de su sección; el resto son
  // catálogos del residencial en solo lectura (el backend acota dispositivos y
  // visitas de proveedores a su sección).
  can('manage', Subjects.RfidCredential);
  can('read', Subjects.AccessDevice);
  can('read', Subjects.Supplier);
  can('read', Subjects.SupplierEmployee);
  can('read', Subjects.SupplierVisit);
  can('read', Subjects.Routine);
  can('read', Subjects.RoutineExecution);
  can('read', Subjects.NotificationConfig);
  can('read', Subjects.Settings);
  can('read', Subjects.FiscalData);

  // Notificaciones - lectura y marcar como leídas
  can('read', Subjects.Notification);
  can('update', Subjects.Notification);

  // Perfil propio
  can('read', Subjects.Profile);
  can('manage', Subjects.Profile);
}

/**
 * Permisos de Guardia de seguridad
 */
function defineGuardAbilities(
  can: CanFunction,
  cannot: CannotFunction,
  user: AbilityUser
) {
  // Dashboard - solo lectura
  can('read', Subjects.Dashboard);

  // Guard Panel - acceso completo
  can('read', Subjects.GuardPanel);

  // Control de acceso - acceso completo
  can('read', Subjects.AccessControl);
  can('manage', Subjects.AccessLog);
  can('read', Subjects.AccessDevice);

  // Invitados - solo lectura (no puede crear ni eliminar)
  can('read', Subjects.Guest);
  cannot('create', Subjects.Guest);
  cannot('delete', Subjects.Guest);

  // Tickets - solo los propios (SDD 4.3)
  can('manage', Subjects.Ticket, { createdBy: user.id });

  // Restricciones/Lista negra - acceso completo
  can('manage', Subjects.Restriction);

  // Turnos - puede iniciar, consultar y cerrar su turno
  can('create', Subjects.Shift);
  can('read', Subjects.Shift);
  can('update', Subjects.Shift);

  // Alertas de emergencia - puede crear
  can('create', Subjects.EmergencyAlert);
  can('read', Subjects.EmergencyAlert);

  // Bitácora - acceso completo
  can('manage', Subjects.Logbook);
  can('manage', Subjects.PatrolCheckpoint);
  can('manage', Subjects.PatrolRound);

  // Proveedores - lectura y registro de visitas
  can('read', Subjects.Supplier);
  can('read', Subjects.SupplierEmployee);
  can('create', Subjects.SupplierVisit);
  can('update', Subjects.SupplierVisit);
  can('read', Subjects.SupplierVisit);

  // Inventario - lectura y movimientos de stock
  can('read', Subjects.Asset);
  can('read', Subjects.AssetCategory);
  can('read', Subjects.AssetMaintenance);
  can('create', Subjects.AssetMaintenance);
  can('read', Subjects.Consumable);
  can('create', Subjects.StockMovement);
  can('read', Subjects.StockMovement);

  // Cámaras en Vivo - lectura
  can('read', Subjects.Camera);

  // Notificaciones - lectura y marcar como leídas
  can('read', Subjects.Notification);
  can('update', Subjects.Notification, { userId: user.id });

  // Perfil propio - puede ver y editar su perfil
  can('read', Subjects.Profile);
  can('read', Subjects.User, { id: user.id });
  can('manage', Subjects.Profile, { id: user.id });

  // Sin acceso a:
  cannot('read', Subjects.Amenity);
  cannot('read', Subjects.Reservation);
  cannot('read', Subjects.Payment);
  cannot('read', Subjects.Charge);
  cannot('read', Subjects.Announcement);
  cannot('read', Subjects.Document);
  cannot('manage', Subjects.Unit);
  cannot('read', Subjects.Settings);
  cannot('read', Subjects.AuditLog);
  cannot('read', Subjects.Report);
  cannot('read', Subjects.FiscalData);
}

/**
 * Permisos de Miembro del Hogar (FAMILIAR)
 * Permisos limitados - solo puede gestionar invitados, ver comunicados   y encuestas
 */
function defineFamiliarAbilities(
  can: CanFunction,
  cannot: CannotFunction,
  user: AbilityUser
) {
  // Perfil propio - puede ver y editar su perfil
  can('read', Subjects.Profile);
  can('read', Subjects.User, { id: user.id });
  can('update', Subjects.User, { id: user.id });
  can('update', Subjects.Profile, { id: user.id });

  // Unidad - solo lectura
  can('read', Subjects.Unit, { id: user.unitId });

  // Control de acceso - página /accesos + historial propio
  can('read', Subjects.AccessControl);

  // Amenidades - puede ver y reservar todas las reservas de su unidad
  can('read', Subjects.Amenity);
  can('manage', Subjects.Reservation, { unitId: user.unitId });

  // Miembros del hogar, mascotas y vehículos - solo lectura
  can('read', Subjects.HouseholdMember);
  cannot('create', Subjects.HouseholdMember);
  cannot('update', Subjects.HouseholdMember);
  cannot('delete', Subjects.HouseholdMember);

  can('read', Subjects.Pet);
  cannot('create', Subjects.Pet);
  cannot('update', Subjects.Pet);
  cannot('delete', Subjects.Pet);

  can('read', Subjects.Vehicle);
  cannot('create', Subjects.Vehicle);
  cannot('update', Subjects.Vehicle);
  cannot('delete', Subjects.Vehicle);

  // Control de acceso - solo lectura de historial
  can('read', Subjects.AccessLog, { userId: user.id });

  // Invitados - acceso completo a sus propios invitados
  can('manage', Subjects.Guest, { hostId: user.id });

  // Comunicados - solo lectura
  can('read', Subjects.Announcement);

  // Transparencia - solo lectura + comentar/preguntar
  can('read', Subjects.TransparencyProject);
  can('create', Subjects.TransparencyComment);
  can('read', Subjects.TransparencyComment);
  can('delete', Subjects.TransparencyComment, { userId: user.id });

  // Encuestas - solo lectura, sin votar
  can('read', Subjects.Poll);

  // Notificaciones - lectura y marcar como leídas
  can('read', Subjects.Notification);
  can('update', Subjects.Notification, { userId: user.id });

  // Incidentes - puede reportar y gestionar los propios (igual que RESIDENT)
  can('read', Subjects.Ticket);
  can('manage', Subjects.Ticket, { createdBy: user.id });

  // Sin acceso a:
  cannot('update', Subjects.Poll);
  cannot('read', Subjects.Dashboard);
  cannot('read', Subjects.Payment);
  cannot('read', Subjects.Charge);
  cannot('read', Subjects.Document);
  cannot('read', Subjects.Pet);
  // cannot('read', Subjects.AccessControl); // FAMILIAR sí puede acceder a /accesos
  // cannot('read', Subjects.AccessControl);
  // cannot('read', Subjects.AccessDevice);
  cannot('manage', Subjects.User);
  cannot('manage', Subjects.Unit);
  cannot('read', Subjects.Settings);
  cannot('read', Subjects.AuditLog);
  cannot('read', Subjects.Report);
  // Datos fiscales - solo lectura, sin edición
  can('read', Subjects.FiscalData, { userId: user.id });
  cannot('create', Subjects.FiscalData);
  cannot('update', Subjects.FiscalData);
  cannot('delete', Subjects.FiscalData);
}

/**
 * Permisos de Residente
 */
function defineResidentAbilities(
  can: CanFunction,
  cannot: CannotFunction,
  user: AbilityUser
) {
  // Dashboard - solo lectura
  can('read', Subjects.Dashboard);

  // Control de acceso - solo gestión de QR e invitados
  can('read', Subjects.AccessControl);

  // Invitados - solo los propios
  can('manage', Subjects.Guest, { hostId: user.id });

  // Comunicados - solo lectura
  can('read', Subjects.Announcement);
  can('read', Subjects.Poll);
  can('update', Subjects.Poll); // Para votar

  // Transparencia - solo lectura + comentar/preguntar
  can('read', Subjects.TransparencyProject);
  can('create', Subjects.TransparencyComment);
  can('read', Subjects.TransparencyComment);
  can('delete', Subjects.TransparencyComment, { userId: user.id });

  // Notificaciones - lectura y marcar como leídas
  can('read', Subjects.Notification);
  can('update', Subjects.Notification, { userId: user.id });

  // Amenidades - lectura y reservar
  can('read', Subjects.Amenity);
  can('manage', Subjects.Reservation, { userId: user.id });

  // Pagos - solo los propios
  can('read', Subjects.Payment, { userId: user.id });
  can('read', Subjects.Charge, { unitId: user.unitId });
  can('create', Subjects.Payment); // Para registrar pagos

  // Tickets - solo los propios
  can('read', Subjects.Ticket);
  can('manage', Subjects.Ticket, { createdBy: user.id });

  // Documentos - solo lectura
  can('read', Subjects.Document);

  // Perfil propio
  can('read', Subjects.Profile);
  can('read', Subjects.User, { id: user.id });
  can('manage', Subjects.Profile, { id: user.id });

  // Mascotas propias
  can('manage', Subjects.Pet, { ownerId: user.id });

  // Reportes - solo su estado de cuenta
  can('read', Subjects.Report);

  // Datos fiscales propios
  can('manage', Subjects.FiscalData, { userId: user.id });

  // Sin acceso a administración
  cannot('manage', Subjects.User);
  cannot('manage', Subjects.Unit);
  cannot('read', Subjects.Settings);
  cannot('read', Subjects.AuditLog);
}

/**
 * Verifica si el usuario puede realizar una acción en un sujeto
 */
export function canDo(
  ability: AppAbility,
  action: Action,
  subject: string,
  field?: string
): boolean {
  return ability.can(action, subject, field);
}

/**
 * Obtiene todas las acciones permitidas para un sujeto
 */
export function getAllowedActions(ability: AppAbility, subject: string): Action[] {
  const actions: Action[] = ['create', 'read', 'update', 'delete'];
  return actions.filter((action) => ability.can(action, subject));
}
